import React, { useState } from 'react';
import { MessageCircle, X, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { supportAPI } from '../lib/api';
import { cn } from '../lib/utils';

const categories = [
    { value: 'order_issue', label: 'Rendeléssel kapcsolatos' },
    { value: 'question', label: 'Kérdés' },
    { value: 'feedback', label: 'Visszajelzés' },
    { value: 'other', label: 'Egyéb' },
];

export function SupportButton() {
    const { isAuthenticated } = useAuth();
    const [open, setOpen] = useState(false);
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ subject: '', category: 'question', message: '', order_id: '' });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.subject.trim() || !form.message.trim()) return;
        setLoading(true);
        try {
            await supportAPI.createTicket({
                ...form,
                order_id: form.order_id.trim() || null,
            });
            setSent(true);
            setForm({ subject: '', category: 'question', message: '', order_id: '' });
            setTimeout(() => { setSent(false); setOpen(false); }, 2500);
        } catch (err) {
            alert('Hiba történt. Kérjük próbáld újra.');
        } finally {
            setLoading(false);
        }
    };

    if (!isAuthenticated) return null;

    return (
        <>
            {/* Floating button */}
            <button
                onClick={() => setOpen(!open)}
                className={cn(
                    "fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110",
                    open ? "bg-zinc-700 rotate-0" : "bg-cyan-600 hover:bg-cyan-500"
                )}
                data-testid="support-fab"
            >
                {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
            </button>

            {/* Support panel */}
            {open && (
                <div className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-48px)] bg-background border border-border rounded-xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200" data-testid="support-panel">
                    <div className="bg-cyan-600 px-5 py-4">
                        <h3 className="text-white font-semibold text-base">Segítségre van szükséged?</h3>
                        <p className="text-cyan-100 text-xs mt-0.5">Küldj nekünk üzenetet!</p>
                    </div>

                    {sent ? (
                        <div className="flex flex-col items-center justify-center py-12 px-6">
                            <CheckCircle className="w-12 h-12 text-green-500 mb-3" />
                            <p className="font-medium text-center">Üzeneted elküldve!</p>
                            <p className="text-sm text-muted-foreground text-center mt-1">Hamarosan válaszolunk.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="p-4 space-y-3">
                            <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1 block">Kategória</label>
                                <select
                                    value={form.category}
                                    onChange={e => setForm(f => ({...f, category: e.target.value}))}
                                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                    data-testid="support-category-select"
                                >
                                    {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1 block">Tárgy</label>
                                <input
                                    type="text"
                                    value={form.subject}
                                    onChange={e => setForm(f => ({...f, subject: e.target.value}))}
                                    placeholder="Miben segíthetünk?"
                                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                    required
                                    data-testid="support-subject-input"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1 block">Rendelés azonosítója (opcionális)</label>
                                <input
                                    type="text"
                                    value={form.order_id}
                                    onChange={e => setForm(f => ({...f, order_id: e.target.value}))}
                                    placeholder="pl. NXSR-002..."
                                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                    data-testid="support-order-input"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1 block">Üzenet</label>
                                <textarea
                                    value={form.message}
                                    onChange={e => setForm(f => ({...f, message: e.target.value}))}
                                    placeholder="Írd le részletesen..."
                                    rows={4}
                                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
                                    required
                                    data-testid="support-message-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={loading || !form.subject.trim() || !form.message.trim()}
                                className="w-full bg-cyan-600 hover:bg-cyan-500"
                                data-testid="support-submit-btn"
                            >
                                {loading ? 'Küldés...' : <><Send className="w-4 h-4 mr-2" /> Üzenet küldése</>}
                            </Button>
                        </form>
                    )}
                </div>
            )}
        </>
    );
}
